/**
 * sharedMorning — pure view-model for the Club "this morning" open-water card.
 *
 * Turns one derived ClubSession (many members' swims for a shared morning) into
 * the display shape the card renders: the shared water-temp fact, the viewer's
 * own route uuid, and the who-swam roster. PURE and unit-tested — the Svelte
 * card only binds this to markup.
 *
 * Fact authority (spec §"the this morning card"): shared facts (temp) resolve by
 * a single deterministic rule — the VIEWER's own value if present, else the
 * EARLIEST starter's, else omit. groupSessions() returns entries ascending by
 * start, so entries[0] is the earliest starter. Distance is per-swimmer (a
 * roster-row stat), never a card fact. The native route is the VIEWER's own
 * (you can't render a route you don't have).
 */
import { localDayKey, type ClubSession, type MemberSwim } from "./clubSessions";
import type { AttendanceEntry, LapLabel, SwimFirehoseResponse, WorkoutSummary } from "@contract/types";
import { openWaterTemp, formatWaterTemp } from "./openWaterHeader";
import { deriveWorkout, type SetGroup } from "./lapAnalysis";
import type { ClubSessionSource } from "./clubSessionSource";

/** What SharedMorningView reports upward (ClubTab) about the shown morning, so
 *  the page resolves its front-door state and builds the sets comparison from
 *  the SAME source the day card renders — one loader, one truth (item 2a). */
export interface SharedMorningSnapshot {
  loading: boolean;
  failed: boolean;
  sessions: ClubSession[];
  shown: ClubSession | null;
  morning: SharedMorning | null;
  source: ClubSessionSource | null;
}

export interface RosterEntry {
  memberId: string;
  /** This member's swim uuid for THIS session — the stable key for the roster
   *  list (a member can log two swims in one session window; memberId is not
   *  unique, uuid is). */
  swimUUID: string;
  label: string;
  /** Whole meters for this member's swim, or null when there is no positive total —
   *  including EVERY tap-attendee row (roster honesty: no stat line, never ranked). */
  distanceMeters: number | null;
  /** Whole seconds of this member's swim, or null for a tap-attendee row (no swim). */
  durationSeconds: number | null;
  isViewer: boolean;
  /** The app's OWN watch-segment sets for this swim (showcase-no-fork: reuses
   *  deriveWorkout, never a hand-built copy) — non-null ONLY for a POOL session
   *  entry with a resolvable firehose; null for open-water entries (rendered via
   *  the route card instead) and for stat-less tap-attendee rows (no swim to
   *  derive from). Empty array is a real, renderable "no sets" result. */
  setGroups: SetGroup[] | null;
}

export interface SharedMorning {
  session: ClubSession;
  /** Shared water-temp fact as a display label ("73°F"), or null when neither the
   *  viewer nor the earliest starter carries a temp. */
  waterTempLabel: string | null;
  /** The viewer's own open-water workout uuid (their route), or null when the
   *  viewer didn't swim this session — no own route to show. */
  routeWorkoutUUID: string | null;
  /** Who swam, ascending by start. No rank. */
  roster: RosterEntry[];
}

export function buildSharedMorning(
  session: ClubSession,
  viewerId: string,
  labelFor: (memberId: string) => string,
  firehoseFor: (memberId: string, workoutUUID: string) => SwimFirehoseResponse | null,
  attendance: AttendanceEntry[] = [],
  labelsFor: (memberId: string, workoutUUID: string) => LapLabel[] = () => [],
): SharedMorning {
  const isPoolSession = session.locationType === "pool";
  const roster: RosterEntry[] = session.entries.map((e) => ({
    memberId: e.memberId,
    swimUUID: e.swim.uuid,
    label: labelFor(e.memberId),
    distanceMeters: distanceOf(e.swim),
    durationSeconds: durationOf(e.swim),
    isViewer: e.memberId === viewerId,
    setGroups: isPoolSession ? setGroupsFor(e.memberId, e.swim.uuid, firehoseFor, labelsFor) : null,
  }));

  // Tap-attendees ("I was there", Plan 5): appended AFTER swimmers, for the shown
  // session's LOCAL day only. A member who swam AND tapped shows once, as a swimmer
  // (audit-ratified spec reading). distanceMeters stays null — roster honesty: a
  // name in the crew, NO stat line, NO marker of lack, never ranked. swimUUID gets a
  // synthetic-but-unique key (there is no swim to key the row by).
  const sessionDay = localDayKey(session.startDate);
  const seated = new Set(roster.map((r) => r.memberId));
  for (const a of attendance) {
    if (a.day !== sessionDay || seated.has(a.memberId)) continue;
    seated.add(a.memberId); // duplicate entries collapse to one row
    roster.push({
      memberId: a.memberId,
      swimUUID: `attendance-${a.day}-${a.memberId}`,
      label: labelFor(a.memberId),
      distanceMeters: null,
      durationSeconds: null,
      isViewer: a.memberId === viewerId,
      setGroups: null, // no swim to derive from — a tap has no firehose
    });
  }

  return {
    session,
    waterTempLabel: resolveTempLabel(session.entries, viewerId, firehoseFor),
    routeWorkoutUUID: viewerRouteUUID(session.entries, viewerId),
    roster,
  };
}

/** Clubmates with a swim on the shown morning: roster rows that are not the viewer and
 *  carry a distance (tap-attendees carry none). Whether this phone holds their firehose is
 *  a separate question (compare needs it); "Only you swam" must never read while one of
 *  these rows is on the card (B-021 class, build 12). */
export function clubmateSwimRows(morning: SharedMorning | null): RosterEntry[] {
  return morning?.roster.filter((r) => !r.isViewer && r.distanceMeters !== null) ?? [];
}

function durationOf(swim: WorkoutSummary): number | null {
  const d = swim.durationSeconds;
  return Number.isFinite(d) && d > 0 ? Math.round(d) : null;
}

function distanceOf(swim: WorkoutSummary): number | null {
  const d = swim.totalDistance;
  return d && Number.isFinite(d.value) && d.value > 0 ? Math.round(d.value) : null;
}

/** Derive this swim's set groups from its firehose, reusing the app's OWN
 *  deriveWorkout (showcase-no-fork), with the SWIMMER's saved stroke corrections
 *  (stroke-sync 2026-09-12: a fixed stroke must read the same on the club strip
 *  as on the owner's Swim tab). No labels = the watch's strokes. A missing
 *  firehose degrades to null — the caller renders the roster row without a strip. */
function setGroupsFor(
  memberId: string,
  workoutUUID: string,
  firehoseFor: (memberId: string, workoutUUID: string) => SwimFirehoseResponse | null,
  labelsFor: (memberId: string, workoutUUID: string) => LapLabel[],
): SetGroup[] | null {
  const fh = firehoseFor(memberId, workoutUUID);
  if (!fh) return null;
  // No lengths recorded (B-019): deriveWorkout would synthesize one whole-swim
  // "set"; the card says "No lengths recorded" for an empty list instead.
  if (!hasLengths(fh)) return [];
  return deriveWorkout(fh, labelsFor(memberId, workoutUUID)).setGroups;
}

/** True when the watch recorded at least one length (a `lap` event). The native
 *  club snapshot sends `sets: []` on exactly the same rule. */
export function hasLengths(fh: SwimFirehoseResponse): boolean {
  return fh.events.some((e) => e.type === "lap");
}

/** Viewer's temp if present, else the earliest starter's, else null. entries are
 *  ascending by start (groupSessions guarantee), so entries[0] is earliest. We
 *  consult ONLY those two members — the spec rule names no third fallback. The
 *  firehose is fetched per SWIM (memberId + that swim's uuid), never "the member's
 *  one firehose": a member swims many mornings, so the temp must come from THIS
 *  session's swim. */
function resolveTempLabel(
  entries: MemberSwim[],
  viewerId: string,
  firehoseFor: (memberId: string, workoutUUID: string) => SwimFirehoseResponse | null,
): string | null {
  const candidates: MemberSwim[] = [];
  const viewer = entries.find((e) => e.memberId === viewerId);
  if (viewer) candidates.push(viewer);
  if (entries[0].memberId !== viewerId) candidates.push(entries[0]);
  for (const c of candidates) {
    const fh = firehoseFor(c.memberId, c.swim.uuid);
    if (fh) {
      const label = formatWaterTemp(openWaterTemp(fh));
      if (label) return label;
    }
  }
  return null;
}

function viewerRouteUUID(entries: MemberSwim[], viewerId: string): string | null {
  const viewer = entries.find((e) => e.memberId === viewerId);
  return viewer ? viewer.swim.uuid : null;
}
